import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Box,
  TextField,
  InputAdornment,
  makeStyles,
  Collapse,
} from "@material-ui/core";
import {
  HelpOutline as HelpIcon,
  Videocam as VideoIcon,
  Gavel as GavelIcon,
  Info as InfoIcon,
  FlashOn as FlashIcon,
  Search as SearchIcon,
  FileCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  commandChip: {
    fontFamily: "monospace",
    fontWeight: "bold",
    marginRight: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  dialogContent: {
    padding: theme.spacing(0, 1, 2, 1),
    minWidth: 400,
  },
  searchField: {
    margin: theme.spacing(1, 2),
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  copyHint: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
}));

const commandSections = [
  {
    title: "Videochamadas BBB",
    icon: <VideoIcon />,
    commands: [
      { cmd: "#reuniao", args: "[titulo]", desc: "Criar reuniao de atendimento com gravacao" },
      { cmd: "#audiencia", args: "[titulo]", desc: "Criar audiencia virtual (gravada, links separados)" },
      { cmd: "#encerrar-reuniao", args: "<ID>", desc: "Encerrar uma reuniao ou audiencia ativa" },
    ],
  },
  {
    title: "Atendimento Juridico",
    icon: <GavelIcon />,
    commands: [
      { cmd: "#protocolo", args: "", desc: "Gerar numero de protocolo do atendimento" },
      { cmd: "#status", args: "", desc: "Ver status, prioridade e agente do ticket" },
      { cmd: "#prioridade", args: "<urgente|alta|normal|baixa>", desc: "Alterar prioridade do atendimento" },
      { cmd: "#transferir", args: "[ID setor]", desc: "Transferir para outro setor (sem ID lista setores)" },
      { cmd: "#encerrar", args: "[motivo]", desc: "Encerrar o atendimento com motivo opcional" },
    ],
  },
  {
    title: "Informacoes",
    icon: <InfoIcon />,
    commands: [
      { cmd: "#horario", args: "", desc: "Ver agendamentos futuros do contato" },
      { cmd: "#fila", args: "", desc: "Info da fila e quantidade em espera" },
      { cmd: "#agente", args: "", desc: "Info do agente responsavel pelo ticket" },
      { cmd: "#dados", args: "", desc: "Ver dados cadastrados do contato" },
    ],
  },
  {
    title: "Acoes Rapidas",
    icon: <FlashIcon />,
    commands: [
      { cmd: "#lembrete", args: "<DD/MM/AAAA> <HH:MM> <msg>", desc: "Agendar lembrete para o contato" },
      { cmd: "#nota", args: "<texto>", desc: "Adicionar nota interna ao atendimento" },
      { cmd: "#tag", args: "[ID]", desc: "Adicionar tag ao ticket (sem ID lista todas)" },
      { cmd: "#rapida", args: "[codigo]", desc: "Usar resposta rapida (sem codigo lista todas)" },
      { cmd: "#ajuda", args: "", desc: "Mostrar todos os comandos no WhatsApp" },
    ],
  },
];

const WhatsAppCommandsPanel = ({ onInsertCommand }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (title) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleCopyCommand = (cmd) => {
    if (onInsertCommand) {
      onInsertCommand(cmd + " ");
    } else {
      navigator.clipboard.writeText(cmd);
    }
  };

  const filteredSections = commandSections
    .map((section) => ({
      ...section,
      commands: section.commands.filter(
        (c) =>
          !search ||
          c.cmd.toLowerCase().includes(search.toLowerCase()) ||
          c.desc.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((s) => s.commands.length > 0);

  return (
    <>
      <Tooltip title="Comandos WhatsApp">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <HelpIcon />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Comandos WhatsApp</Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Digite no chat do WhatsApp. Clique para copiar.
          </Typography>
        </DialogTitle>
        <div className={classes.dialogContent}>
          <TextField
            className={classes.searchField}
            size="small"
            variant="outlined"
            placeholder="Buscar comando..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredSections.map((section, idx) => (
            <div key={section.title}>
              {idx > 0 && <Divider />}
              <div
                className={classes.sectionHeader}
                onClick={() => toggleSection(section.title)}
              >
                <span className={classes.sectionIcon}>{section.icon}</span>
                <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 600 }}>
                  {section.title}
                </Typography>
                <Chip
                  size="small"
                  label={section.commands.length}
                  variant="outlined"
                />
                {expandedSections[section.title] !== false ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </div>
              <Collapse in={expandedSections[section.title] !== false}>
                <List dense>
                  {section.commands.map((c) => (
                    <ListItem
                      key={c.cmd}
                      button
                      onClick={() => handleCopyCommand(c.cmd)}
                    >
                      <ListItemText
                        primary={
                          <Box>
                            <Chip
                              size="small"
                              label={c.cmd}
                              className={classes.commandChip}
                              color="primary"
                              variant="outlined"
                            />
                            {c.args && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                component="span"
                              >
                                {c.args}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={c.desc}
                      />
                      <ListItemIcon style={{ minWidth: 30 }}>
                        <Tooltip title="Copiar comando">
                          <CopyIcon fontSize="small" color="action" />
                        </Tooltip>
                      </ListItemIcon>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </div>
          ))}
          <Box px={2} pt={1}>
            <Typography className={classes.copyHint}>
              Os comandos sao processados automaticamente quando enviados pelo WhatsApp.
              O cliente recebe a resposta diretamente na conversa.
            </Typography>
          </Box>
        </div>
      </Dialog>
    </>
  );
};

export default WhatsAppCommandsPanel;
